import { heardGameText, heardOutcome, parseHeardGame, type HeardOutcome } from './heardGame';
import { destination, parseOffset } from './geo/offset';

/** Transkriptionsrad under jakt — samma form som i KartaTab. */
export type HuntTranscriptEntry = { speaker: string; text: string; ts: number; lat?: number; lng?: number };

/** Slutsats som Q-assistenten kan visa i jaktchatten (byggs heuristiskt på transkription). */
export type HuntAssistantInsight = {
  id: string;
  text: string;
  inferredFrom: string;
  gameType?: string;
  isKill?: boolean;
  /** QV1: vad som hände (fällt, skadskjutet, skott, obs) och viltets typ ur det som hördes. */
  outcome?: HeardOutcome;
  family?: string;
  subcat?: string;
  kon?: 'Hane' | 'Hona';
  alder?: 'Ungdjur' | 'Vuxet';
  lat?: number;
  lng?: number;
};

export const killPat = /\b(sköt|skjutit|skjuten|skjutit på|påskjut|påskjuten|skott|fällt|fälld|träffad|nedlagd|nedlagt|knäckt|tog ner|prick|skutt|lagt ned|ligger|liggande|är nere|var nere|döende|dött|dog)\b/i;
export const obsPat = /\b(ser|såg|sedd|obs|observation|observerade?|passerade?|passerar|sprang|rörde sig|syntes|syns|hann se|noterade?|märkte|drog förbi|hos mig|vid mig|pass)\b/i;
const drevPat = /\b(drev|driv|driva|sätt(er)?\s+igång|kör\s+igång)\b/i;
const dirPat = /\b(norra?|södra?|syd|norr|öster|väster|väst|öst|nv\b|sv\b|no\b|sö\b)\b/i;

/** Transkriptionsrad som telefonen skapar när ljudmätningen hör ett skott (voiceActivity.ts). */
export const SHOT_EVENT_TEXT = 'Skottljud registrerat';
export function isShotEntry(entry: { text: string }): boolean {
  return entry.text === SHOT_EVENT_TEXT;
}

/** Tid (ms) under vilken en obs-detektion för ett viltslag undertrycks efter en kill-detektion. */
const KILL_OBS_SUPPRESS_MS = 120_000;

/**
 * Extraherar korta slutsatser (observation, skott/fällning, drev m.m.) från transkriptionsrader.
 * Deterministisk så varje klient får samma id för samma rad + kategori.
 */
export function extractAssistantInsights(entries: HuntTranscriptEntry[]): HuntAssistantInsight[] {
  const out: HuntAssistantInsight[] = [];
  const seen = new Set<string>();
  // Undertryck obs-rader för ett viltslag under en tid efter att en kill detekterats
  const lastKillTs = new Map<string, number>();

  for (const entry of entries) {
    if (isShotEntry(entry)) {
      const id = `q:shot:${entry.ts}:${entry.speaker}`;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({
        id,
        text: `Skott hört vid ${entry.speaker}s position (registrerat av telefonen).`,
        inferredFrom: entry.speaker,
        gameType: 'Skott',
        isKill: false,
        lat: entry.lat,
        lng: entry.lng,
      });
      continue;
    }
    const game = parseHeardGame(entry.text);
    const outcome = game ? heardOutcome(entry.text) : null;
    // Fällt och skott registreras som fällt vilt; skadskjutet blir en obs med eftersök (ingen status för det).
    const isKill = outcome === 'fallt' || outcome === 'skott';

    if (game) {
      const art = game.label;
      const prevKillTs = lastKillTs.get(art);
      const suppressedByRecentKill = !isKill && outcome !== 'skadskjutet' && prevKillTs != null && entry.ts - prevKillTs < KILL_OBS_SUPPRESS_MS;
      if (suppressedByRecentKill) continue; // dubblettskydd: obs direkt efter kill → hoppa över

      if (isKill) lastKillTs.set(art, entry.ts);

      const what = heardGameText(game);
      let text: string;
      if (outcome === 'fallt') text = `Bekräftat fällt ${what} (tolkat från ljud/text).`;
      else if (outcome === 'skott') text = `Påskjutet eller skott mot ${what} (tolkat från ljud/text).`;
      else if (outcome === 'skadskjutet') text = `Skadskjutet ${what} – eftersök? (tolkat från ljud/text).`;
      else text = `Observation av ${what} (tolkat från ljud/text).`;
      const kind = isKill ? 'k' : outcome === 'skadskjutet' ? 's' : 'o';
      const id = `q:${entry.ts}:${entry.speaker}:${art}:${kind}`;
      if (seen.has(id)) continue;
      seen.add(id);
      // QA1: "100 m norr om mig ser jag två rådjur" → observationen 100 m norr om den som pratar, inte där hen står.
      let { lat, lng } = entry;
      if (!isKill && lat != null && lng != null) {
        const off = parseOffset(entry.text);
        if (off?.ok) {
          ({ lat, lng } = destination({ lat, lng }, off.offset.bearing, off.offset.meters));
          text = text.replace(/ \(tolkat från ljud\/text\)\.$/, ` ca ${off.offset.label} ${entry.speaker} (tolkat från ljud/text).`);
        }
      }
      out.push({
        id, text, inferredFrom: entry.speaker, gameType: art, isKill, outcome: outcome ?? 'obs',
        family: game.family, subcat: game.subcat, kon: game.kon, alder: game.alder, lat, lng,
      });
    }

    if (drevPat.test(entry.text)) {
      const dm = entry.text.match(dirPat);
      const dir = dm ? dm[0] : null;
      const id = `q:drev:${entry.ts}:${entry.speaker}:${entry.text.slice(0, 48)}`;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({
        id,
        text: dir
          ? `Drev rapporterat — riktning/område nämnt: «${dir}» (tolkat från ljud/text).`
          : 'Drev rapporterat eller påbörjat (tolkat från ljud/text).',
        inferredFrom: entry.speaker,
      });
    }
  }
  return out;
}

/**
 * Röstkommando för jaktens livekamera i en egen transkriberad rad:
 * "filma" / "starta kameran" → 'start', "sluta filma" / "stäng av kameran" → 'stop'.
 */
export function parseCameraCommand(text: string): 'start' | 'stop' | null {
  const t = text.toLowerCase();
  if (/(?:^|[^\p{L}])(sluta|slutar|stoppa|avsluta|stäng(?:\s+av)?)\s+(?:att\s+)?(film(?:a|en|ningen)?|kameran|videon)(?![\p{L}])/u.test(t)) return 'stop';
  if (/(?:^|[^\p{L}])(?:inte|aldrig)\s+(?:\p{L}+\s+)?filma(?![\p{L}])/u.test(t)) return null;
  if (/(?:^|[^\p{L}])(filma|börja\s+filma|starta\s+(?:kameran|videon|filmen)|slå\s+på\s+kameran)(?![\p{L}])/u.test(t)) return 'start';
  return null;
}
